import { useEffect } from 'react';
import { useGmailConnectionStore } from '@/stores';

export type GmailConnectionState =
  | 'connected'
  | 'not_connected'
  | 'paused'
  | 'expired'
  | 'expiring_soon'
  | 'needs_attention'
  | 'revoked';

export type GmailConnectionStatus =
  | { state: 'connected' }
  | { state: 'not_connected' }
  | { state: 'paused' }
  | { state: 'expired' }
  | { state: 'expiring_soon'; daysUntilExpiry: number }
  // `needs_attention` is a RECOVERABLE renewal failure: the watch re-arm
  // failed but the refresh_token is still valid (active=true, not revoked).
  // A one-tap server refresh fixes it — no Google re-consent. Lower
  // severity than expired/revoked, which are checked first.
  | { state: 'needs_attention' }
  // `revoked` means Google answered a refresh with invalid_grant — the
  // user pulled Yaruta's OAuth grant or the token aged out. Takes
  // priority over `active=true` because the stored token is dead even
  // if the row hasn't been marked inactive yet.
  | { state: 'revoked' };

export interface GmailConnectionResult {
  data: GmailConnectionStatus | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Single source of truth for Gmail connection state.
 *
 * Backed by `useGmailConnectionStore` so all consumers share one fetch
 * and stay in sync after refetch (e.g. after OAuth reconnect).
 */
export function useGmailConnectionStatus(
  householdId: string | null | undefined,
): GmailConnectionResult {
  const data = useGmailConnectionStore((s) => s.data);
  const isLoading = useGmailConnectionStore((s) => s.isLoading);
  const error = useGmailConnectionStore((s) => s.error);
  const loadedFor = useGmailConnectionStore((s) => s.loadedFor);
  const load = useGmailConnectionStore((s) => s.load);
  const refetch = useGmailConnectionStore((s) => s.refetch);

  useEffect(() => {
    void load(householdId);
  }, [householdId, load]);

  // If the store currently holds data for a different household, suppress
  // it for this caller so we don't briefly show stale state on switch.
  const matches = loadedFor === householdId;

  return {
    data: matches ? data : undefined,
    isLoading: isLoading || !matches,
    error: matches ? error : null,
    refetch: () => void refetch(),
  };
}
